import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { usePageTitle } from 'utils/pageTitle';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';

import {
	regenerateApiKey,
	fetchUser,
	updateUser,
	forgotPassword,
} from 'reducers/userReducer';
import { fetchCurrentSubscription } from 'data/reducers/subscriptionReducer';
import { API_URL, setApiDomain } from 'data/services/apiConstants';
import { isAdminUser } from 'utils/userUtils';
import { isValidEmail } from 'utils/validators';
import Alert from 'components/Alert';
import Spinner from 'components/Spinner';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import {
	PaymentHistory,
	SubscriptionHistory,
	SmsAddonCard,
	SubscriptionCard,
} from './Subscription';
import {
	getSmsAddonState,
	getSmsAddonPeriodCost,
} from './Subscription/smsAddon';

import SmsAlertsOptInCheckbox, {
	isSmsOptInValid,
	phoneRequiresSmsOptIn,
} from 'view/components/SmsAlertsOptInCheckbox';

import './Settings.css';

const Settings = (props) => {
	usePageTitle('Account Settings');
	const dispatch = useDispatch();
	const {
		apiKey,
		isRegeneratingApiKey,
		apiKeyRegenError,
		isLoading,
		isUpdating,
		userData,
		authToken,
		isAdmin,
		isResettingPassword,
		wasResetSuccessful,
		passwordResetMessages,
		currentPlan,
		currentSubscription,
		isLoadingSubscription,
	} = useSelector((state) => ({
		apiKey: state.user.userData?.api_key?.api_key || '',
		isRegeneratingApiKey: state.user.isRegeneratingApiKey,
		apiKeyRegenError: state.user.apiKeyRegenError,
		isLoading: state.user.isLoading || state.user.isUpdating,
		isUpdating: state.user.isUpdating,
		userData: state.user.userData,
		authToken: state.user.authToken,
		isAdmin: isAdminUser(state.user.userData),
		isResettingPassword: state.user.isResettingPassword,
		wasResetSuccessful: state.user.wasResetSuccessful,
		passwordResetMessages: state.user.passwordResetMessages,
		currentPlan: state.subscription?.currentSubscription?.plan,
		currentSubscription: state.subscription?.currentSubscription,
		isLoadingSubscription: state.subscription?.isLoadingCurrentSubscription,
	}));

	const hasActiveSubscription = Boolean(currentSubscription?.id);
	const isEnterprisePlan =
		(currentPlan?.name || '').toLowerCase().includes('enterprise') ||
		currentPlan?.id === 'enterprise' ||
		currentPlan?.slug === 'enterprise';

	const plan = currentSubscription?.plan;
	const subscription =
		currentSubscription?.subscription || currentSubscription;
	// Only show billing details for a real subscription; never a placeholder plan or $0 charge
	const showBillingDetails = hasActiveSubscription && !isLoadingSubscription;
	let subscriptionPlanName = 'No active subscription';
	if (isLoadingSubscription) {
		subscriptionPlanName = 'Loading subscription...';
	} else if (hasActiveSubscription) {
		subscriptionPlanName = plan?.name || 'Current plan';
	}
	const interval =
		currentSubscription?.interval || plan?.interval || 'monthly';
	const isYearly = interval === 'yearly';
	const billingType = isYearly ? 'Yearly' : 'Monthly';
	const basePrice = hasActiveSubscription ? (plan?.price_dollars || 0) : 0;
	// The SMS add-on bills on top of the plan; show the combined recurring charge
	const { hasSmsAddon } = getSmsAddonState(currentSubscription);
	const price = hasSmsAddon
		? (Number(basePrice) + getSmsAddonPeriodCost(isYearly)).toFixed(2)
		: basePrice;
	const nextBillingDate = subscription?.next_billing_date || 'Unknown';
	const currentPlanNameDisplay = showBillingDetails ? plan?.name || 'Current plan' : 'None';

	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [phone, setPhone] = useState('');
	const [smsAlertsOptIn, setSmsAlertsOptIn] = useState(false);
	const [smsOptInError, setSmsOptInError] = useState(false);
	const [isEmailValid, setIsEmailValid] = useState('');
	const [isSuccess, setIsSuccess] = useState(false);
	const [isEmailConfRequired, setIsEmailConfRequired] = useState(false);
	const [apiUrlText, setApiUrlText] = useState(API_URL);
	const [isChangingApi, setIsChangingApi] = useState(false);
	const [activeTab, setActiveTab] = useState('payments');

	useEffect(() => {
		if (userData?.email) {
			setEmail(userData.email);
			setIsEmailValid(isValidEmail(userData.email));
		}
		if (userData?.name) {
			setName(userData.name);
		}
		if (userData?.phone) {
			setPhone(userData.phone);
		}
		if (userData?.sms_alerts_opt_in) {
			setSmsAlertsOptIn(true);
		}
	}, [userData]);

	useEffect(() => {
		if (authToken) {
			dispatch(fetchUser(authToken));
		}
		dispatch(fetchCurrentSubscription());
	}, [dispatch, authToken]);

	const onChangeEmail = (emailString) => {
		setEmail(emailString);
		setIsEmailValid(isValidEmail(emailString));
	};

	const onSubmit = () => {
		// Phone is optional: the SMS opt-in rule only applies once a number is entered
		if (phone && !isSmsOptInValid(phone, smsAlertsOptIn)) {
			setSmsOptInError(true);
			return;
		}

		setSmsOptInError(false);
		setIsSuccess(false);
		const userUpdate = {
			name,
			email,
			phone,
			sms_alerts_opt_in: smsAlertsOptIn,
		};
		dispatch(updateUser(userUpdate)).then((res) => {
			if (res?.type === 'UPDATE_USER_SUCCESS') {
				if (res.apiResponse?.email_change_verification_required) {
					setIsEmailConfRequired(true);
				} else {
					setIsEmailConfRequired(false);
				}
				setIsSuccess(true);
			}
		});
	};

	const canSave = !phone || isSmsOptInValid(phone, smsAlertsOptIn);
	const showSmsOptInError =
		smsOptInError || (phoneRequiresSmsOptIn(phone) && !smsAlertsOptIn);

	const setApi = (url) => {
		setIsChangingApi(true);
		setApiDomain(url);
		setTimeout(() => {
			setApiUrlText(API_URL);
			setIsChangingApi(false);
		}, 1000);
	};

	const confirmModalChildren = (newUrl) => (
		<div>
			<div>Are you sure you want to change the app's API URL to:</div>
			<div className="mt-1">
				<strong>{newUrl}</strong>
			</div>
			<div className="mt-3">
				Changing this to an invalid URL could make the app unusable for you!
			</div>
			<div className="mt-2">
				This can be undone by entering the following command into the browser's
				developer console:
			</div>
			<div className="mt-2">
				<pre>resetApiUrl()</pre>
			</div>
		</div>
	);

	const confirmApiChange = (newUrl) => {
		return dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Confirm API URL Change',
				onConfirm: () => setApi(newUrl),
				children: confirmModalChildren(newUrl),
				confirmLabel: 'Confirm',
			})
		);
	};

	return (
		<DashboardLayout>
			<div className="violerts-v3-root rd-settings-v3 tw-w-full">
				<h1 className="!tw-ml-0 tw-text-2xl tw-font-bold tw-text-text-primary tw-mb-8">
					Settings
				</h1>

				{isSuccess && (
					<div className="tw-mb-6">
						<Alert color={isEmailConfRequired ? 'info' : 'success'}>
							{isEmailConfRequired ? (
								<div>
									Email verification required to complete this update. Check your email
									for instructions.
								</div>
							) : (
								<div>User info updated successfully!</div>
							)}
						</Alert>
					</div>
				)}

				{isLoading && !name && !email ? (
					<div className="tw-py-12 tw-text-center">
						<Spinner />
					</div>
				) : (
					<div>
						{/* 1. Account Info (3-column input row + SMS opt-in) */}
						<div className="tw-mb-8">
							<div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-4">
								<div>
									<label className="tw-block tw-text-sm tw-font-medium tw-text-text-primary tw-mb-1.5">
										Name
									</label>
									<input
										type="text"
										className="tw-w-full tw-rounded-[4px] tw-border tw-border-[#C4C4C4] tw-bg-[#FCFCFC] tw-px-3 tw-py-2 tw-text-sm tw-text-text-primary focus:tw-border-[#007BFF] focus:tw-outline-none"
										value={name}
										disabled={isLoading}
										onChange={(ev) => setName(ev.target.value)}
										placeholder="Your name"
									/>
								</div>
								<div>
									<label className="tw-block tw-text-sm tw-font-medium tw-text-text-primary tw-mb-1.5">
										Email
									</label>
									<input
										type="email"
										className={`tw-w-full tw-rounded-[4px] tw-border tw-bg-[#FCFCFC] tw-px-3 tw-py-2 tw-text-sm tw-text-text-primary focus:tw-border-[#007BFF] focus:tw-outline-none ${
											isEmailValid ? 'tw-border-[#C4C4C4]' : 'tw-border-red-400'
										}`}
										value={email}
										disabled={isLoading}
										onChange={(ev) => onChangeEmail(ev.target.value)}
										placeholder="Email"
									/>
								</div>
								<div>
									<label className="tw-block tw-text-sm tw-font-medium tw-text-text-primary tw-mb-1.5">
										Phone Number (optional)
									</label>
									<input
										type="text"
										className="tw-w-full tw-rounded-[4px] tw-border tw-border-[#C4C4C4] tw-bg-[#FCFCFC] tw-px-3 tw-py-2 tw-text-sm tw-text-text-primary focus:tw-border-[#007BFF] focus:tw-outline-none"
										value={phone}
										disabled={isLoading}
										onChange={(ev) => {
											setPhone(ev.target.value);
											if (!phoneRequiresSmsOptIn(ev.target.value)) {
												setSmsOptInError(false);
											}
										}}
										placeholder="555-123-1234"
									/>
								</div>
							</div>
							<div className="tw-mb-4">
								<SmsAlertsOptInCheckbox
									id="settings-sms-alerts-opt-in"
									checked={smsAlertsOptIn}
									onChange={(ev) => {
										setSmsAlertsOptIn(ev.target.checked);
										if (ev.target.checked) {
											setSmsOptInError(false);
										}
									}}
									disabled={isLoading}
									showError={showSmsOptInError}
								/>
							</div>
							<div>
								<button
									className="tw-bg-[#161616] tw-text-white tw-rounded-[4px] tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium hover:tw-bg-black disabled:tw-opacity-50"
									onClick={onSubmit}
									disabled={isLoading || !canSave}
								>
									{isUpdating ? 'Saving...' : 'Save'}
								</button>
							</div>
						</div>

						{/* 2. Update Password */}
						<div className="tw-mb-8">
							<h2 className="!tw-ml-0 tw-text-xl tw-font-bold tw-text-text-primary tw-mb-1">
								Update Password
							</h2>
							<p className="tw-text-sm tw-text-text-secondary tw-mb-3">
								An email containing a link to update your password will be sent to
								your email associated to your account.
							</p>
							{isResettingPassword && (
								<div className="tw-my-2">
									<Spinner />
								</div>
							)}
							{passwordResetMessages.map((message) => (
								<div key={message} className="tw-mb-3">
									<Alert
										color={wasResetSuccessful ? 'success' : 'danger'}
										text={message}
									/>
								</div>
							))}
							<button
								onClick={() => dispatch(forgotPassword(userData?.email))}
								disabled={isResettingPassword || !userData?.email}
								className="tw-bg-[#ADDBFF] tw-text-[#161616] tw-border tw-border-[#007BFF] hover:tw-bg-[#9AC3E3] tw-rounded-[4px] tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-medium disabled:tw-opacity-50"
							>
								{isResettingPassword ? 'Sending...' : 'Change password'}
							</button>
						</div>

						{/* 3. Airtable Access Key */}
						<div className="tw-mb-8">
							<label className="tw-block tw-text-sm tw-font-medium tw-text-text-primary tw-mb-1.5">
								Airtable Access Key
							</label>
							{apiKeyRegenError && (
								<div className="tw-mb-2">
									<Alert text={apiKeyRegenError} color="danger" />
								</div>
							)}
							<div className="tw-mb-3">
								<input
									type="text"
									readOnly
									className="tw-w-full tw-max-w-md tw-rounded-[4px] tw-border tw-border-[#C4C4C4] tw-bg-[#FCFCFC] tw-px-3 tw-py-2 tw-text-sm tw-text-text-primary focus:tw-outline-none"
									value={apiKey}
								/>
							</div>
							<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
								<button
									disabled={isRegeneratingApiKey}
									className="tw-bg-white tw-text-text-primary tw-border tw-border-[#C4C4C4] hover:tw-bg-[#F7F7F7] tw-rounded-[4px] tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-medium disabled:tw-opacity-50"
									onClick={() => dispatch(regenerateApiKey())}
								>
									{isRegeneratingApiKey
										? 'Regenerating...'
										: 'Regenerate Airtable Key'}
								</button>
								{/* Airtable sync is enterprise-only */}
								{isEnterprisePlan && (
									<button
										className="tw-bg-white tw-text-text-primary tw-border tw-border-[#C4C4C4] hover:tw-bg-[#F7F7F7] tw-rounded-[4px] tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-medium disabled:tw-opacity-50"
										onClick={() => dispatch(openModal(MODAL_TYPES.AIRTABLE_SYNC))}
										disabled={!apiKey}
									>
										Sync with Airtable
									</button>
								)}
								<Link
									to="/airtable-instructions"
									className="tw-text-sm tw-text-[#007BFF] hover:tw-underline"
								>
									How to connect to Airtable
								</Link>
							</div>
						</div>

						{/* 4. Plan & Subscription (2 Side-by-Side Cards) */}
						<div className="tw-mb-8">
							<div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-4">
								<h2 className="!tw-ml-0 tw-text-xl tw-font-bold tw-text-text-primary tw-m-0">
									{subscriptionPlanName}
								</h2>
								{showBillingDetails && (
									<span className="tw-inline-flex tw-items-center tw-bg-[#FFBDC9] tw-text-[#161616] tw-border tw-border-[#FE8AA1] tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium">
										{billingType}
									</span>
								)}
							</div>

							<div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
								{/* Left Card: Next Billing Date */}
								<div className="tw-rounded-[8px] tw-border tw-border-[#D9D9D9] tw-bg-[#F7F7F7] tw-p-5 tw-flex tw-flex-col tw-justify-between">
									<div>
										<h3 className="!tw-ml-0 tw-text-base tw-font-bold tw-text-text-primary tw-mb-2">
											Next Billing Date
										</h3>
										{isLoadingSubscription ? (
											<Spinner size="sm" className="tw-mb-4" />
										) : (
											<p className="tw-text-sm tw-text-text-secondary tw-mb-4">
												{showBillingDetails
													? `You’ll be charged $${price} for the ${subscriptionPlanName} Plan`
													: 'There is no upcoming charge.'}
											</p>
										)}
									</div>
									{showBillingDetails && (
										<div>
											<span className="tw-inline-flex tw-items-center tw-bg-[#ADDBFF] tw-text-[#161616] tw-border tw-border-[#007BFF] tw-rounded-full tw-px-3 tw-py-0.5 tw-text-xs tw-font-medium">
												{nextBillingDate}
											</span>
										</div>
									)}
								</div>

								{/* Right Card: Manage Subscription */}
								<div className="tw-rounded-[8px] tw-border tw-border-[#D9D9D9] tw-bg-[#F7F7F7] tw-p-5 tw-flex tw-flex-col tw-justify-between">
									<div>
										<h3 className="!tw-ml-0 tw-text-base tw-font-bold tw-text-text-primary tw-mb-2">
											Manage Subscription
										</h3>
										<div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-text-sm tw-text-text-primary">
											<span>Current Plan:</span>
											<span className="tw-inline-flex tw-items-center tw-bg-[#ADDBFF] tw-text-[#161616] tw-border tw-border-[#007BFF] tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium">
												{currentPlanNameDisplay}
											</span>
										</div>
										<p className="tw-text-sm tw-text-text-secondary tw-mb-4">
											{isLoadingSubscription
												? null
												: hasActiveSubscription
												? (isEnterprisePlan
													? "You're on a custom Enterprise plan. To modify your subscription or limits, please contact our support team."
													: 'To change or cancel your subscription plan, please contact our support team.')
												: 'No active subscription is currently on file.'}
										</p>
									</div>
									<div>
										<SubscriptionCard />
									</div>
								</div>
							</div>

							{/* SMS Instant Alerts add-on (shown only when the backend offers it) */}
							<SmsAddonCard />
						</div>

						{/* 5. Payments & Subscriptions History Tabs */}
						<div className="tw-mb-8">
							{/* Segmented control: the container owns the border and the rounded outer
								corners and clips the buttons, so the middle edge stays square */}
							<div className="tw-inline-flex tw-items-stretch tw-mb-3 tw-overflow-hidden tw-rounded-[4px] tw-border tw-border-[#C4C4C4] tw-bg-white">
								<button
									type="button"
									onClick={() => setActiveTab('payments')}
									aria-pressed={activeTab === 'payments'}
									className={`!tw-rounded-none tw-border-r tw-border-[#C4C4C4] tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors ${
										activeTab === 'payments'
											? 'tw-bg-[#161616] tw-text-white'
											: 'tw-bg-white tw-text-text-primary hover:tw-bg-[#F7F7F7]'
									}`}
								>
									Payments
								</button>
								<button
									type="button"
									onClick={() => setActiveTab('subscriptions')}
									aria-pressed={activeTab === 'subscriptions'}
									className={`!tw-rounded-none tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors ${
										activeTab === 'subscriptions'
											? 'tw-bg-[#161616] tw-text-white'
											: 'tw-bg-white tw-text-text-primary hover:tw-bg-[#F7F7F7]'
									}`}
								>
									Subscriptions
								</button>
							</div>

							<div className="tw-rounded-[8px] tw-border tw-border-[#D9D9D9] tw-bg-[#F7F7F7] tw-p-5">
								{activeTab === 'payments' && (
									<PaymentHistory onDownloadInvoice={(paymentId) => {}} />
								)}
								{activeTab === 'subscriptions' && <SubscriptionHistory />}
							</div>
						</div>

						{/* 6. Change API Server (Admin only) */}
						{isAdmin && (
							<div className="tw-mt-10 tw-border-t tw-border-[#EDEDED] tw-pt-8">
								<h2 className="!tw-ml-0 tw-text-xl tw-font-bold tw-text-text-primary tw-mb-4">
									Change API Server
								</h2>
								<div className="tw-mb-4">
									<label className="tw-block tw-text-sm tw-font-medium tw-text-text-primary tw-mb-1.5">
										API URL
									</label>
									<input
										type="text"
										className="tw-w-full tw-rounded-[4px] tw-border tw-border-[#C4C4C4] tw-bg-[#FCFCFC] tw-px-3 tw-py-2 tw-text-sm tw-text-text-primary focus:tw-border-[#007BFF] focus:tw-outline-none"
										value={apiUrlText}
										disabled={isChangingApi}
										onChange={(ev) => setApiUrlText(ev.target.value)}
									/>
								</div>
								<div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
									<button
										disabled={isChangingApi}
										className="tw-bg-[#161616] tw-text-white tw-rounded-[4px] tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium hover:tw-bg-black disabled:tw-opacity-50"
										onClick={() => confirmApiChange(apiUrlText)}
									>
										Update API URL
									</button>
									<button
										disabled={isChangingApi}
										className="tw-bg-white tw-text-text-primary tw-border tw-border-[#C4C4C4] tw-rounded-[4px] tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium hover:tw-bg-[#F7F7F7] disabled:tw-opacity-50"
										onClick={() => setApi(null)}
									>
										Reset to Default
									</button>
								</div>
								<div className="tw-space-y-1 tw-text-xs tw-text-text-secondary tw-leading-relaxed">
									<p>
										This is for debugging API server migration. Change this to the
										URL of the API server you want to use. Including a protocol (e.g
										"https://") is required.
									</p>
									<p>
										The current URLs for test servers are
										"https://api-new.violerts.com" and "https://ecs.violerts.com". The
										default url is "https://api.violerts.com".
									</p>
									<p>
										The URL will persist when you refresh the page or open the app
										in a new tab, even if you are logged out.
									</p>
									<p className="tw-text-red-600">
										Warning! Entering an invalid URL may make the app unusable for
										you!
									</p>
									<p>
										This can be manually undone from the browser console by entering
										the command "resetApiUrl()".
									</p>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</DashboardLayout>
	);
};

export default Settings;
